import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import {
  DEFAULT_LANGUAGE,
  DEFAULT_NAMESPACE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  normalizeLanguage,
  type LanguageCode,
} from './config';

import enCommon from './locales/en/common.json';
import heCommon from './locales/he/common.json';
import enAuth from './locales/en/auth.json';
import heAuth from './locales/he/auth.json';
import enTaskpane from './locales/en/taskpane.json';
import heTaskpane from './locales/he/taskpane.json';
import enMcc from './locales/en/mcc.json';
import heMcc from './locales/he/mcc.json';

/**
 * Translation resources. Namespaces are added as feature areas are migrated.
 * Keep the `en` and `he` trees identical.
 * Remaining namespaces (rephrasing, ask) are registered as their feature
 * areas are migrated.
 */
const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    taskpane: enTaskpane,
    mcc: enMcc,
  },
  he: {
    common: heCommon,
    auth: heAuth,
    taskpane: heTaskpane,
    mcc: heMcc,
  },
} as const;

const registeredNamespaces = Object.keys(resources.en);

if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      detection: {
        // Browser / Office language is ignored so the product default (Hebrew)
        // wins until the user (or their saved preference) chooses otherwise.
        order: ['localStorage'],
        lookupLocalStorage: LANGUAGE_STORAGE_KEY,
        caches: ['localStorage'],
      },
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
      load: 'languageOnly',
      nonExplicitSupportedLngs: true,
      ns: registeredNamespaces,
      defaultNS: DEFAULT_NAMESPACE,
      interpolation: {
        escapeValue: false,
      },
      returnNull: false,
      react: {
        useSuspense: false,
      },
    });
}

/**
 * Apply the active language onto the document root.
 *
 * Only `lang` follows the UI language. The layout direction is deliberately
 * pinned to `ltr`: switching the UI language changes the *text* only, never the
 * task pane layout. RTL content still renders correctly at the text level via
 * the browser's bidi algorithm (and `dir="auto"` on text nodes), keeping
 * direction a property of the content rather than the layout.
 */
export function applyDocumentLanguage(code: string): void {
  const normalized = normalizeLanguage(code);
  const root = document.documentElement;
  root.lang = normalized;
  root.dir = 'ltr';
}

/**
 * Resolve and apply the initial UI language for the add-in.
 *
 * Priority: explicit localStorage choice > product default (Hebrew).
 * The authenticated user's server preference is applied later by
 * `syncLanguageFromUser` once the profile loads.
 */
export function initAddinLanguage(): void {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  } catch {
    stored = null;
  }

  const initial = normalizeLanguage(stored);
  if (i18n.language !== initial) {
    void i18n.changeLanguage(initial);
  }
  applyDocumentLanguage(initial);
}

/** Apply the user's saved server-side language preference, if present. */
export function syncLanguageFromUser(userMetadata: unknown): void {
  const language = (userMetadata as { preferences?: { language?: string } } | null | undefined)
    ?.preferences?.language;
  if (!language) return;
  setUiLanguage(normalizeLanguage(language));
}

/**
 * Set the UI language locally (localStorage + i18n). Server persistence is
 * handled by callers that own the user preference (Profile / Settings).
 */
export function setUiLanguage(code: LanguageCode): void {
  const normalized = normalizeLanguage(code);
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, normalized);
  } catch {
    // Ignore storage failures (private mode, quota, etc.).
  }
  if (i18n.language !== normalized) {
    void i18n.changeLanguage(normalized);
  }
  applyDocumentLanguage(normalized);
}

// Keep the document direction in sync on every language change.
i18n.on('languageChanged', (lng) => applyDocumentLanguage(lng));

export default i18n;
